import { useEffect, useState } from "react";
import { Modal } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";
import { LoadingBlurDiv } from "@shared/components";

import { useAdminStructureQuery } from "../../api/structures/structures.queries";
import type { AdminStructureDetail } from "../../api/structures/structures.types";
import { AdminStructureGeneralDataFormProvider } from "./AdminStructureGeneralDataFormProvider";
import {
  AdminStructureGeneralDataFooter,
  AdminStructureGeneralDataTypeField,
} from "./AdminStructureGeneralDataTab";
import { AdminStructureDetailModalOpenPitShell } from "./AdminStructureDetailModalOpenPitShell";
import type { StructureModalTabId } from "./admin-structure-modal-tabs.config";
import { AdminStructureModalTabs } from "./AdminStructureModalTabs";

export type AdminStructureDetailModalProps = {
  open: boolean;
  onClose: () => void;
  complexId: string;
  structureId: string | null;
  canWrite: boolean;
  /** Detalhe carregado (GET Y=21) — omitir para buscar via query */
  detail?: AdminStructureDetail | null;
};

const AdminStructureDetailModal = ({
  open,
  onClose,
  complexId,
  structureId,
  canWrite,
  detail: detailProp,
}: AdminStructureDetailModalProps) => {
  const { translate } = useLocalization();
  const shouldFetch = open && detailProp === undefined;
  const { data: fetchedDetail, isFetching } = useAdminStructureQuery(
    shouldFetch ? complexId : null,
    shouldFetch ? structureId : null,
  );

  const [resolvedDetail, setResolvedDetail] = useState<AdminStructureDetail | null>(
    () => (detailProp !== undefined ? (detailProp ?? null) : null),
  );
  const [activeTab, setActiveTab] = useState<StructureModalTabId>("generalData");
  const [isTabSaving, setIsTabSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    document.body.classList.add("admin-structure-detail-modal-open");
    return () => {
      document.body.classList.remove("admin-structure-detail-modal-open");
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setResolvedDetail(null);
      setActiveTab("generalData");
      setIsTabSaving(false);
      return;
    }
    if (detailProp !== undefined) {
      setResolvedDetail(detailProp ?? null);
      return;
    }
    if (fetchedDetail) {
      setResolvedDetail(fetchedDetail);
    }
  }, [open, detailProp, fetchedDetail]);

  useEffect(() => {
    setIsTabSaving(false);
  }, [activeTab]);

  if (!open || !structureId) {
    return null;
  }

  return (
    <div data-testid="admin-structure-detail-modal">
      <Modal
        open={open}
        onClose={onClose}
        title={translate("admin.structures.modal.title")}
        content={
          resolvedDetail ? (
            <AdminStructureGeneralDataFormProvider
              complexId={complexId}
              structureId={structureId}
              canWrite={canWrite}
              detail={resolvedDetail}
              onSaved={setResolvedDetail}
              embedForm={false}
            >
              <LoadingBlurDiv isLoading={isFetching && !resolvedDetail}>
                <div className="flex flex-col gap-4 px-6 pb-10 pt-2">
                  <div className="w-full max-w-[550px] shrink-0">
                    <AdminStructureGeneralDataTypeField />
                  </div>
                  <AdminStructureDetailModalOpenPitShell activeTab={activeTab}>
                    <AdminStructureModalTabs
                      onActiveTabChange={setActiveTab}
                      onTabSavingChange={setIsTabSaving}
                    />
                    <AdminStructureGeneralDataFooter
                      onCancel={onClose}
                      variant="modal"
                      activeTab={activeTab}
                      isTabSaving={isTabSaving}
                    />
                  </AdminStructureDetailModalOpenPitShell>
                </div>
              </LoadingBlurDiv>
            </AdminStructureGeneralDataFormProvider>
          ) : (
            <LoadingBlurDiv isLoading={isFetching}>
              <div className="min-h-[240px] px-6 py-4" />
            </LoadingBlurDiv>
          )
        }
      />
    </div>
  );
};

export default AdminStructureDetailModal;
